import { useEffect, useRef, useState, type FormEvent } from 'react'
import { apiFetch } from '../../lib/api'
import type { HeldBookItem } from './bookWorkApi'

interface Props {
  item: HeldBookItem
  scope: { fund_id?: string | null; client_id?: string | null }
  onPendingChange: (pending: boolean) => void
  onSaved?: () => void
  hasSaved?: boolean
}

interface DraftResult {
  request_id: string
  item_id: string
  status: string
  requested_document: string
  outbound_available: false
  financial_authority: false
}

export default function HeldRequestDraft({
  item,
  scope,
  onPendingChange,
  onSaved,
  hasSaved,
}: Props) {
  const [description, setDescription] = useState('')
  const [counterparty, setCounterparty] = useState('')
  const [pending, setPending] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const active = useRef(false)
  const inFlight = useRef(false)
  const attempt = useRef<{ input: string; id: string } | null>(null)
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (inFlight.current || saved || !description.trim() || !counterparty.trim()) return
    const input = JSON.stringify({
      revision: item.revision,
      scope,
      requested_document: description.trim(),
      counterparty_name: counterparty.trim(),
    })
    if (attempt.current?.input !== input) attempt.current = { input, id: crypto.randomUUID() }
    const command = attempt.current
    inFlight.current = true
    setPending(true)
    setError('')
    onPendingChange(true)
    try {
      const result = await apiFetch<DraftResult>(
        `/api/rk/decks/group/${encodeURIComponent(item.group_id)}/held/${encodeURIComponent(item.item_id)}/document-request`,
        { method: 'POST', body: JSON.stringify({ ...JSON.parse(input), command_id: command.id }) }
      )
      if (
        result.request_id !== command.id ||
        result.item_id !== item.item_id ||
        result.status !== 'draft' ||
        result.requested_document !== description.trim() ||
        result.outbound_available !== false ||
        result.financial_authority !== false
      )
        throw new Error('The draft could not be confirmed. Refresh before continuing.')
      if (active.current) {
        setSaved(true)
        onSaved?.()
      }
    } catch (failure) {
      if (active.current)
        setError(failure instanceof Error ? failure.message : 'Could not save the draft.')
    } finally {
      inFlight.current = false
      if (active.current) {
        setPending(false)
        onPendingChange(false)
      }
    }
  }

  if (saved)
    return (
      <p role="status">
        Document request draft saved. It has not been sent. This review remains open.
      </p>
    )
  return (
    <form onSubmit={submit} aria-label="Prepare document request">
      <label>
        Document needed
        <textarea
          value={description}
          maxLength={2000}
          required
          disabled={pending}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>
      <label>
        Counterparty
        <input
          value={counterparty}
          maxLength={500}
          required
          disabled={pending}
          onChange={(event) => setCounterparty(event.target.value)}
        />
      </label>
      <p>This saves a draft. Sending is not yet available for this review.</p>
      <button type="submit" disabled={pending || !description.trim() || !counterparty.trim()}>
        {pending
          ? 'Saving draft…'
          : hasSaved
            ? 'Prepare another document request'
            : 'Prepare document request'}
      </button>
      {error ? <p role="alert">{error}</p> : null}
    </form>
  )
}
